import { supabase } from './supabase';

export const getTrainingModules = () =>
  supabase.from('training_modules').select('*, linked_policy:policies(section_number, title)').order('module_number');

export const getStaffTrainingRecords = () =>
  supabase.from('staff_training_records').select('*, staff:staff(full_name, position_code, status), training_module:training_modules(module_number, title, sequence, course_id)').order('due_date');

export const assignTraining = ({ staffId, trainingModuleId, dueDate, reasonNote, assignedBy }) =>
  supabase.from('staff_training_records').insert([{
    staff_id: staffId, training_module_id: trainingModuleId, status: 'assigned',
    assigned_at: new Date().toISOString(), assigned_by: assignedBy,
    due_date: dueDate || null, reason_note: reasonNote || null,
  }]).select().single();

// ── Courses ("Learning Paths") — a bundle of modules assigned/certified/renewed
// as one unit, matching industry LMS conventions, while each module inside it
// still tracks its own completion (staff_training_records, unchanged above).
export const getTrainingCourses = () =>
  supabase.from('training_courses').select('*, training_modules(id, module_number, title, sequence)').order('title');

export const getStaffCourseRecords = () =>
  supabase.from('staff_course_records').select('*, staff:staff(full_name, position_code, status), course:training_courses(title, code)');

// Assigns every module in the course at once (one staff_course_records row +
// one staff_training_records row per module) — "assign the training" is a
// single action, not one per section.
export const assignCourse = async ({ staffId, courseId, dueDate, reasonNote, assignedBy }) => {
  const { data: courseRecord, error: courseErr } = await supabase.from('staff_course_records')
    .upsert([{ staff_id: staffId, course_id: courseId, status: 'assigned', assigned_at: new Date().toISOString(), assigned_by: assignedBy }],
      { onConflict: 'staff_id,course_id', ignoreDuplicates: false })
    .select().single();
  if (courseErr) return { error: courseErr };

  const { data: modules } = await supabase.from('training_modules').select('id').eq('course_id', courseId);
  const created = [];
  for (const m of modules || []) {
    const { data: existing } = await supabase.from('staff_training_records')
      .select('id').eq('staff_id', staffId).eq('training_module_id', m.id).maybeSingle();
    if (existing) continue;
    const { data: record, error } = await assignTraining({ staffId, trainingModuleId: m.id, dueDate, reasonNote, assignedBy });
    // A DB unique constraint on (staff_id, training_module_id) backs up the
    // existence check above — 23505 here just means a concurrent assign call
    // won the race for this exact module between our SELECT and INSERT. The
    // record exists either way, so this isn't a real failure.
    if (error && error.code !== '23505') return { error };
    if (record) created.push(record);
  }
  return { data: { courseRecord, created } };
};

// Resets a course a staff member is already enrolled in — clears completion
// on the course record and every one of its section records so a fresh
// assignCourse() call (called by the caller right after this) rebuilds it
// from scratch. Used for two distinct cases from the "Assign" modal:
//   1. A broken/incomplete assignment (e.g. sections never got created) —
//      assignCourse() alone already self-heals this without calling reset.
//   2. A genuinely completed course that needs to be redone (policy update,
//      corrective retraining) — this is the destructive path; the caller is
//      expected to confirm with the coordinator before calling it, since it
//      clears prior completion/signature/certificate history on the record.
export const resetCourseProgress = async ({ staffId, courseId }) => {
  const { data: modules } = await supabase.from('training_modules').select('id').eq('course_id', courseId);
  const moduleIds = (modules || []).map(m => m.id);
  if (moduleIds.length > 0) {
    const { error: modErr } = await supabase.from('staff_training_records')
      .update({ status: 'assigned', completed_at: null, signature_ref: null, pdf_url: null, verified_by: null })
      .eq('staff_id', staffId).in('training_module_id', moduleIds);
    if (modErr) return { error: modErr };
  }
  const { error: courseErr } = await supabase.from('staff_course_records')
    .update({ status: 'assigned', completed_at: null, pdf_url: null })
    .eq('staff_id', staffId).eq('course_id', courseId);
  if (courseErr) return { error: courseErr };
  return { data: true };
};

export const certificationStatus = (cert) => {
  const today = new Date().toISOString().split('T')[0];
  if (cert.override_active && cert.override_expires_at && cert.override_expires_at >= today) {
    return { key: 'override', label: 'Compliant (Override)', color: 'var(--copper)', bg: 'var(--copper-bg)' };
  }
  // A row can exist before the credential is actually obtained (e.g. the
  // onboarding "Request TB Screening/CPR" flow) — distinct from "no expiry
  // date needed" (hha_competency/other), which really is just compliant.
  if (cert.status === 'requested') return { key: 'requested', label: 'Requested', color: 'var(--warn)', bg: 'var(--warn-bg)' };
  if (!cert.expiry_date) return { key: 'compliant', label: 'Compliant', color: 'var(--success)', bg: 'var(--success-bg)' };
  if (cert.expiry_date < today) return { key: 'expired', label: 'Expired', color: 'var(--danger)', bg: 'var(--danger-bg)' };
  const in30 = new Date(); in30.setDate(in30.getDate() + 30);
  if (cert.expiry_date <= in30.toISOString().split('T')[0]) return { key: 'expiring', label: 'Expiring', color: 'var(--warn)', bg: 'var(--warn-bg)' };
  return { key: 'valid', label: 'Valid', color: 'var(--success)', bg: 'var(--success-bg)' };
};

// Race-safe "find or create" for a staff member's active training-link token.
// A DB partial unique index (staff_documents(staff_id, doc_type) WHERE
// status='sent') backs this: if two assign/reminder actions land at nearly
// the same moment, only one insert can win — the loser's 23505 conflict is
// caught here and resolved by simply re-reading the winner's row, instead of
// producing a second 'sent' token that would make .maybeSingle() elsewhere
// throw on "multiple rows returned" and silently break future reminders.
export const getOrCreateTrainingLinkToken = async (staffId) => {
  const { data: existingDoc } = await supabase.from('staff_documents')
    .select('id, sign_token').eq('staff_id', staffId).eq('doc_type', 'training').eq('status', 'sent').maybeSingle();
  if (existingDoc?.sign_token) return { token: existingDoc.sign_token };

  const token = crypto.randomUUID();
  const { error } = await supabase.from('staff_documents').insert([{
    staff_id: staffId, doc_type: 'training', doc_label: 'Assigned training',
    sign_token: token, status: 'sent', sent_at: new Date().toISOString(),
  }]);
  if (!error) return { token };
  if (error.code !== '23505') return { error };

  // Lost the race — someone else's insert won, read their token instead.
  const { data: winner, error: readErr } = await supabase.from('staff_documents')
    .select('sign_token').eq('staff_id', staffId).eq('doc_type', 'training').eq('status', 'sent').maybeSingle();
  if (readErr || !winner?.sign_token) return { error: readErr || new Error('Could not resolve training link after conflict') };
  return { token: winner.sign_token };
};

export const setOverride = ({ certId, active, reason, expiresAt, overrideBy }) =>
  supabase.from('staff_certifications').update({
    override_active: active,
    override_reason: active ? reason : null,
    override_by: active ? overrideBy : null,
    override_at: active ? new Date().toISOString() : null,
    override_expires_at: active ? expiresAt : null,
  }).eq('id', certId);
